import { useCallback, useEffect, useMemo } from 'react';
import type { SlugifiedPermissions } from '../../lib/api/permission';
import { getUserConfigFromWindow } from '../../lib/lookerConfig';
import { DEFAULT_STALE_TIME } from '../../lib/queryClient';
import { useClusterData } from '../../modules/GenomeWorkspaceView/LeftView/hooks/useClusterData';
import { useUserData } from '../../modules/GenomeWorkspaceView/LeftView/hooks/useUserData';
import { usePermissionsQuery } from '../api';
import { useCurrentWorkspace } from '../useCurrentWorkspace';
import { useFilters } from '../useFilters';
import {
  checkAdvertisersFactory,
  checkLifeScopeFactory,
  checkSignalsFactory,
  checkStudioFactory,
  checkTopLevelPermission,
  getSlugifiedPermissions,
} from './helpers';
import { DefaultPermissionsState, type PermissionSubject, type StudioPermissionState } from './studioPermissionState';

const PERMISSION_STALE_TIME = 2 * DEFAULT_STALE_TIME;

export const usePermissions = () => {
  const { userData, setUser } = useUserData();
  const { filters } = useFilters();

  const userID = Number(userData.embeddedUserId);
  const accountID = Number(userData.acctId);
  const bypassPermissions = userData.permissionFlag === 'N';
  const { currentWorkspace } = useCurrentWorkspace();

  const advertiserID = Number(filters?.pseudo_advertiser_id_filter?.expression ?? null);

  const { hasClusterError, isLoading: isClusterDataLoading } = useClusterData(accountID, advertiserID);

  const {
    data: permissionsData,
    isLoading: isPermissionDataLoading,
    error: permissionsError,
  } = usePermissionsQuery(
    { user_id: userID, account_id: accountID },
    { enabled: !!userID && !!accountID, staleTime: PERMISSION_STALE_TIME },
  );

  const currentWorkspaceRestriction = useMemo(() => {
    return permissionsData?.workspaces?.find((workspace) => workspace.id === currentWorkspace?.workspace_type_id)
      ?.restrictClinicalData;
  }, [permissionsData?.workspaces, currentWorkspace?.workspace_type_id]);

  const permissionState: StudioPermissionState = useMemo(() => {
    if (!permissionsData) {
      return DefaultPermissionsState;
    }

    const { advertisers, lifePermissions, signalPermissions, genomeStudioPermissions } = permissionsData;

    const advertiserPermissions = advertisers?.find((a) => a.advId === advertiserID);

    const slugifiedPermissions: SlugifiedPermissions = {
      lifePermissions: getSlugifiedPermissions(lifePermissions),
      signalPermissions: getSlugifiedPermissions(signalPermissions),
      genomeStudioPermissions: getSlugifiedPermissions(genomeStudioPermissions),
    };

    const checkAdvertisers = checkAdvertisersFactory(advertiserPermissions);
    const checkSignals = checkSignalsFactory(slugifiedPermissions);
    const checkLifeScope = checkLifeScopeFactory(slugifiedPermissions);
    const checkStudio = checkStudioFactory(slugifiedPermissions);

    const ChannelsOperational = !hasClusterError && !isClusterDataLoading;

    const HCP365AccountLevel = checkTopLevelPermission(permissionsData, 'hcp365Account');
    const HCP365UserLevel = checkTopLevelPermission(permissionsData, 'hcp365User');
    const HCP365Status = HCP365AccountLevel && HCP365UserLevel && checkAdvertisers('hcp365Status');

    return {
      Moments: (checkAdvertisers('moments') && checkSignals('moments')) ?? false,
      IBHealth: (checkAdvertisers('ibHealth') && checkSignals('ibHealth')) ?? false,
      Clinical: (checkAdvertisers('claimsData') && checkSignals('claimsData') && !currentWorkspaceRestriction) ?? false,
      NPIListDownload: checkLifeScope('downloadSmartlistNpis') ?? false,
      SmartActionWebHook: checkSignals('smartActionsTriggerWebhook') ?? false,
      SmartActionsAllowed: HCP365Status && (checkSignals('smartActionsTriggerWebhook') ?? false),
      ChannelsOperational: HCP365Status && ChannelsOperational,
      HCP365: HCP365Status,
      NPIDetails: !userData.isInternalUser ? checkStudio('npiDetails') : true,
      NPIProfessions: !userData.isInternalUser ? checkStudio('npiProfessions') : true,
      PatientDetails: !userData.isInternalUser ? checkStudio('patientDetails') : true,
      HCP365AccountLevel,
      ShowPII: (checkLifeScope('npiListManagement') || checkLifeScope('manageNpisInTargeting')) ?? false,
      Reports: checkSignals('reports') ?? false,
    };
  }, [
    permissionsData,
    hasClusterError,
    isClusterDataLoading,
    currentWorkspaceRestriction,
    userData.isInternalUser,
    advertiserID,
  ]);

  const hasPermission = useCallback(
    (subject?: PermissionSubject): boolean => bypassPermissions || (subject ? permissionState[subject] : true),
    [bypassPermissions, permissionState],
  );

  const hasAnyPermissionOf = useCallback(
    (listOfPermissions: PermissionSubject[]): boolean =>
      bypassPermissions || listOfPermissions.some((p) => hasPermission(p)),
    [bypassPermissions, hasPermission],
  );

  const hasAccessToWorkspace = useCallback(
    (workspaceTypeId: number): boolean =>
      (bypassPermissions || permissionsData?.workspaceTypes.includes(workspaceTypeId)) ?? false,
    [permissionsData?.workspaceTypes, bypassPermissions],
  );

  useEffect(() => {
    const getPermissionFlagFromLooker = () => {
      const permissionFlag = getUserConfigFromWindow()?.permissionFlag ?? null;

      setUser({
        permissionFlag: permissionFlag ?? 'Y',
      });
    };

    const interval = setInterval(getPermissionFlagFromLooker, 5 * DEFAULT_STALE_TIME);

    return () => {
      clearInterval(interval);
    };
  }, [setUser]);

  return useMemo(
    () => ({
      userId: permissionsData?.userId,
      email: permissionsData?.email?.toLowerCase(),
      accountName: permissionsData?.accountName,
      accountId: permissionsData?.accountId,
      permissionState,
      hasPermission,
      hasAnyPermissionOf,
      hasAccessToWorkspace,
      isPermissionDataLoading,
      permissionError: permissionsError,
    }),
    [
      permissionsData?.userId,
      permissionsData?.email,
      permissionsData?.accountName,
      permissionsData?.accountId,
      permissionState,
      hasPermission,
      hasAnyPermissionOf,
      hasAccessToWorkspace,
      isPermissionDataLoading,
      permissionsError,
    ],
  );
};
